import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronRight, ArrowRight, ShieldCheck } from 'lucide-react';
import { useUI } from '../../contexts/UIContext';
import { useTranslation } from '../../providers/TranslationProvider';
import { SCREENS } from '../../utils/constants';

// Steps
import Step1Needs from './steps/Step1Needs';
import Step2Habits from './steps/Step2Habits';
import Step3Flow from './steps/Step3Flow';
import Step4Plans from './steps/Step4Plans';
import Step5Updates from './steps/Step5Updates';

const STORAGE_KEY = 'onboarding_progress';

const OnboardingScreen = () => {
    const { navigateToScreen, isDarkMode } = useUI();
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [userFirstName, setUserFirstName] = useState('');

    // Lazy load state
    const [onboardingData, setOnboardingData] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : {
                needs: [],
                morningHabits: [],
                flowHabits: [],
                weeklyPlans: [],
                customPlan: '',
                notificationsEnabled: null
            };
        } catch (e) { return {}; }
    });

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(onboardingData));
    }, [onboardingData]);

    useEffect(() => {
        const storedUser = localStorage.getItem('idapps_user');
        if (storedUser) {
            try {
                const parsed = JSON.parse(storedUser);
                if (parsed.name) setUserFirstName(parsed.name.split(' ')[0]);
            } catch (e) { }
        }
    }, []);

    const steps = [
        {
            component: Step1Needs,
            title: userFirstName ? `${userFirstName}, ${t('onboarding.step1.title_short')}` : t('onboarding.step1.title'),
            subtitle: t('onboarding.step1.subtitle')
        },
        {
            component: Step2Habits,
            title: t('onboarding.step2.title'),
            subtitle: t('onboarding.step2.subtitle')
        },
        {
            component: Step3Flow,
            title: t('onboarding.step3.title'),
            subtitle: t('onboarding.step3.subtitle')
        },
        {
            component: Step4Plans,
            title: t('onboarding.step4.title'),
            subtitle: t('onboarding.step4.subtitle')
        },
        {
            component: Step5Updates,
            title: t('onboarding.step5.title'),
            subtitle: t('onboarding.step5.subtitle')
        },
    ];

    const CurrentComponent = steps[currentStep].component;
    const progress = ((currentStep + 1) / steps.length) * 100;

    const handleUpdate = (newData) => {
        setOnboardingData(prev => ({ ...prev, ...newData }));
    };

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            handleFinish();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) setCurrentStep(prev => prev - 1);
    };

    const handleFinish = () => {
        localStorage.removeItem(STORAGE_KEY);
        navigateToScreen(SCREENS.DASHBOARD);
    };

    const isNextDisabled = () => {
        if (currentStep === 0 && (!onboardingData.needs || onboardingData.needs.length === 0)) return true;
        if (currentStep === 4 && onboardingData.notificationsEnabled === null) return true;
        return false;
    };

    return (
        <div className={`w-full h-screen overflow-hidden flex flex-col items-center p-4 md:p-6 transition-colors duration-500 ${isDarkMode ? 'bg-black text-white' : 'bg-slate-50 text-slate-900'}`}>

            {/* Header: Nav & Progress */}
            <div className="w-full max-w-md shrink-0 z-10 flex flex-col gap-4 mt-2">
                <div className="flex items-center justify-between">
                    <button
                        onClick={handleBack}
                        className={`p-2 rounded-full transition-colors ${currentStep === 0 ? 'opacity-0 pointer-events-none' : isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`}
                    >
                        <ArrowLeft size={20} />
                    </button>

                    <div className={`flex-1 mx-4 h-1 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-slate-200'}`}>
                        <motion.div
                            className="h-full bg-blue-500"
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                        />
                    </div>
                    <div className="w-9" />
                </div>
            </div>

            {/* Main Content: Grow to fill space */}
            <div className="w-full max-w-md flex-1 flex flex-col justify-center min-h-0 z-10">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="flex flex-col h-full justify-center"
                    >
                        <div className="shrink-0 mb-4 text-center md:text-left">
                            <motion.h2
                                className="text-2xl md:text-3xl font-bold leading-tight"
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {steps[currentStep].title}
                            </motion.h2>
                            <motion.p
                                className={`text-sm md:text-base mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                            >
                                {steps[currentStep].subtitle}
                            </motion.p>
                        </div>

                        {/* Scrollable area only if needed inside component, but we aim for fit */}
                        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar py-2">
                            <CurrentComponent
                                data={onboardingData}
                                onUpdate={handleUpdate}
                                isDarkMode={isDarkMode}
                            />
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Footer: Actions */}
            <div className="w-full max-w-md shrink-0 z-10 pt-4 pb- safe-bottom space-y-3">

                {/* Suggest Button */}
                <button
                    onClick={() => {
                        const suggestions = {
                            0: { needs: ['organize', 'routines'] },
                            1: { morningHabits: ['wakeup', 'coffee', 'plan'] },
                            2: { flowHabits: ['work', 'lunch', 'deepwork'] },
                            3: { weeklyPlans: ['work_school', 'home'] },
                            4: { notificationsEnabled: true }
                        };
                        if (suggestions[currentStep]) {
                            handleUpdate(suggestions[currentStep]);
                        }
                    }}
                    className={`w-full py-3 rounded-2xl text-sm font-medium transition-colors border ${isDarkMode
                        ? 'border-white/10 hover:bg-white/5 text-slate-300'
                        : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                        }`}
                >
                    ✨ {t('onboarding.suggest')}
                </button>

                <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={handleNext}
                    disabled={isNextDisabled()}
                    className={`w-full py-3.5 rounded-2xl font-bold text-base flex items-center justify-center gap-2 transition-all ${isNextDisabled()
                        ? 'opacity-50 cursor-not-allowed bg-slate-800 text-white'
                        : 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                        }`}
                >
                    {currentStep === steps.length - 1 ? t('utility.finish', 'Finish') : t('utility.continue', 'Continue')}
                    {currentStep < steps.length - 1 && <ArrowRight size={18} />}
                </motion.button>

                {/* Privacy Badge */}
                <div className="flex items-center justify-center gap-1.5 opacity-40">
                    <ShieldCheck size={12} />
                    <span className="text-[10px] font-medium tracking-wider uppercase">{t('onboarding.privacy_badge')}</span>
                </div>
            </div>
        </div>
    );
};

export default OnboardingScreen;
